import { z } from 'zod';
import type { FicheTicket, PreuveSav, Relance, ResumeTicket, SaisieTicket, Ticket } from '../types';
import {
  COLONNES_CONTACT,
  COLONNES_FICHIER,
  COLONNES_POSITION,
  COLONNES_SITE,
  champsFichier,
  champsPosition,
  ligneContact,
  ligneSite,
  versContact,
  versFichier,
  versPosition,
  versSite,
} from './lignes';

/*
 * Les tickets SAV tels que les renvoie PostgREST (db/007-sav.sql), vérifiés
 * avant usage. Pur, comme lignes-chantiers.ts : ni réseau ni client Supabase.
 */

const COLONNES_TICKET =
  'id, reference, chantier_id, objet, description, priorite, statut, partenaire_id, debut_prevu, fin_prevue, ouvert_le, ouvert_par_nom, demarre_le, resolu_le, resolu_par_nom, rapport, clos_le, clos_par_nom, motif_reouverture, systeme, piece, marque_modele, numero_serie, etat_piece, constate_le, arret, partenaires!tickets_sav_partenaire_id_fkey(raison_sociale), signale:partenaires!tickets_sav_signale_par_fkey(raison_sociale)';

/** Le ticket seul, avec ce qu'en dit son chantier. */
export const SELECTION_TICKET = `${COLONNES_TICKET}, chantiers(reference, entreprise_id, entreprises(raison_sociale), sites(nom, commune, departement, ${COLONNES_POSITION}))`;

/** Pour la liste : le type des preuves seulement, pour compter les photos. */
export const SELECTION_RESUME_TICKET = `${SELECTION_TICKET}, preuves_sav(type)`;

/* Le site entier et les contacts : pour un partenaire, la base ne laisse passer
   que les contacts « sur place ». */
export const SELECTION_FICHE_TICKET = `${COLONNES_TICKET}, chantiers(reference, entreprise_id, entreprises(raison_sociale, contacts(${COLONNES_CONTACT})), sites(${COLONNES_SITE})), preuves_sav(${COLONNES_FICHIER}, ticket_id, type)`;

export const COLONNES_RELANCE = 'id, partenaire_id, sujet, envoyee_le, envoyee_par_nom';

const statutTicket = z.enum(['a-planifier', 'planifie', 'en-cours', 'resolu', 'clos', 'rouvert']);
const typePreuveSav = z.enum(['photo', 'autre']);

const ligneTicketSeul = z.object({
  id: z.string(),
  reference: z.string(),
  chantier_id: z.string(),
  objet: z.string(),
  description: z.string(),
  priorite: z.enum(['normale', 'urgente']),
  statut: statutTicket,
  partenaire_id: z.string().nullable(),
  debut_prevu: z.string().nullable(),
  fin_prevue: z.string().nullable(),
  ouvert_le: z.string(),
  ouvert_par_nom: z.string().nullable(),
  demarre_le: z.string().nullable(),
  resolu_le: z.string().nullable(),
  resolu_par_nom: z.string().nullable(),
  rapport: z.string(),
  clos_le: z.string().nullable(),
  clos_par_nom: z.string().nullable(),
  motif_reouverture: z.string().nullable(),
  systeme: z.enum(['solaire', 'sechage']).nullable().default(null),
  piece: z.string().nullable().default(null),
  marque_modele: z.string().nullable().default(null),
  numero_serie: z.string().nullable().default(null),
  etat_piece: z
    .enum([
      'hors-service',
      'a-remplacer',
      'a-reparer',
      'fonctionne-mal',
      'endommagee',
      'a-verifier',
    ])
    .nullable()
    .default(null),
  constate_le: z.string().nullable().default(null),
  arret: z.enum(['oui', 'partiel', 'non']).nullable().default(null),
  signale: z.object({ raison_sociale: z.string() }).nullable().default(null),
  partenaires: z.object({ raison_sociale: z.string() }).nullable(),
});

const siteResume = z
  .object({ nom: z.string(), commune: z.string(), departement: z.string(), ...champsPosition })
  .nullable();

export const ligneTicket = ligneTicketSeul.extend({
  chantiers: z
    .object({
      reference: z.string(),
      entreprise_id: z.string(),
      entreprises: z.object({ raison_sociale: z.string() }).nullable(),
      sites: siteResume,
    })
    .nullable(),
});

export const ligneResumeTicket = ligneTicket.extend({
  preuves_sav: z.array(z.object({ type: typePreuveSav })),
});

const lignePreuveSav = z.object({ ...champsFichier, ticket_id: z.string(), type: typePreuveSav });

export const ligneFicheTicket = ligneTicketSeul.extend({
  chantiers: z
    .object({
      reference: z.string(),
      entreprise_id: z.string(),
      entreprises: z
        .object({ raison_sociale: z.string(), contacts: z.array(ligneContact) })
        .nullable(),
      sites: ligneSite,
    })
    .nullable(),
  preuves_sav: z.array(lignePreuveSav),
});

export const ligneRelance = z.object({
  id: z.string(),
  partenaire_id: z.string(),
  sujet: z.string(),
  envoyee_le: z.string(),
  envoyee_par_nom: z.string().nullable(),
});

/** Le ticket d'une preuve : son statut, pour ne pas envoyer un fichier refusé. */
export const ligneStatutTicket = z.object({ statut: statutTicket });

// ── Vers le domaine ──────────────────────────────────────────────────────────

type ChantierDuTicket = {
  reference: string;
  entreprise_id: string;
  entreprises: { raison_sociale: string } | null;
  sites: z.infer<typeof siteResume>;
} | null;

function versTicketDe(l: z.infer<typeof ligneTicketSeul>, c: ChantierDuTicket): Ticket {
  return {
    id: l.id,
    reference: l.reference,
    chantierId: l.chantier_id,
    chantierReference: c?.reference ?? '',
    entrepriseId: c?.entreprise_id ?? '',
    entrepriseNom: c?.entreprises?.raison_sociale ?? '',
    siteNom: c?.sites?.nom ?? '',
    commune: c?.sites?.commune ?? '',
    departement: c?.sites?.departement ?? '',
    position: c?.sites ? versPosition(c.sites) : null,
    objet: l.objet,
    description: l.description,
    priorite: l.priorite,
    statut: l.statut,
    partenaireId: l.partenaire_id,
    partenaireNom: l.partenaires?.raison_sociale ?? null,
    debutPrevu: l.debut_prevu,
    finPrevue: l.fin_prevue,
    ouvertLe: l.ouvert_le,
    ouvertPar: l.ouvert_par_nom,
    demarreLe: l.demarre_le,
    resoluLe: l.resolu_le,
    resoluPar: l.resolu_par_nom,
    rapport: l.rapport,
    closLe: l.clos_le,
    closPar: l.clos_par_nom,
    motifReouverture: l.motif_reouverture,
    systeme: l.systeme,
    piece: l.piece,
    signaleParNom: l.signale?.raison_sociale ?? null,
    marqueModele: l.marque_modele,
    numeroSerie: l.numero_serie,
    etatPiece: l.etat_piece,
    constateLe: l.constate_le,
    arret: l.arret,
  };
}

export function versTicket(l: z.infer<typeof ligneTicket>): Ticket {
  return versTicketDe(l, l.chantiers);
}

export function versResumeTicket(l: z.infer<typeof ligneResumeTicket>): ResumeTicket {
  return {
    ...versTicket(l),
    nbPhotos: l.preuves_sav.filter((p) => p.type === 'photo').length,
  };
}

function versPreuveSav(l: z.infer<typeof lignePreuveSav>): PreuveSav {
  return { ...versFichier(l), ticketId: l.ticket_id, type: l.type };
}

/** `null` quand le chantier n'est pas lisible : la fiche n'a alors pas de sens. */
export function versFicheTicket(l: z.infer<typeof ligneFicheTicket>): FicheTicket | null {
  const c = l.chantiers;
  if (c === null) return null;
  const surPlace =
    c.entreprises?.contacts
      .filter((x) => x.sur_place)
      .map(versContact)
      .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))[0] ?? null;
  return {
    ...versTicketDe(l, {
      reference: c.reference,
      entreprise_id: c.entreprise_id,
      entreprises: c.entreprises,
      sites: c.sites,
    }),
    preuves: l.preuves_sav.map(versPreuveSav).sort((a, b) => a.ajouteLe.localeCompare(b.ajouteLe)),
    site: versSite(c.sites),
    contactSurPlace: surPlace,
  };
}

export function versRelance(l: z.infer<typeof ligneRelance>): Relance {
  return {
    id: l.id,
    partenaireId: l.partenaire_id,
    sujet: l.sujet,
    envoyeeLe: l.envoyee_le,
    envoyeePar: l.envoyee_par_nom,
  };
}

// ── Vers la base ─────────────────────────────────────────────────────────────

/** Les seules colonnes que le client écrit (db/007-sav.sql). */
export function ligneDeSaisieTicket(s: SaisieTicket) {
  return {
    chantier_id: s.chantierId,
    objet: s.objet.trim(),
    description: s.description.trim(),
    priorite: s.priorite,
  };
}

/**
 * Le filtre PostgREST de l'historique d'un ticket : le ticket et ses preuves,
 * supprimées comprises (rattachées par `parent_id`).
 */
export function filtreHistoriqueTicket(ticketId: string): string {
  return [
    `and(objet_type.eq.tickets_sav,objet_id.eq.${ticketId})`,
    `and(objet_type.eq.preuves_sav,parent_id.eq.${ticketId})`,
  ].join(',');
}
